import { Box, TextField, TextFieldProps } from "@material-ui/core";
import { addYears, format, isValid, parse, startOfMonth, startOfYear } from "date-fns";
import { memo, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { isEmptyValue } from "../../utils/is-empty-value";
import Year from "../calendar/Year";
import { DateFilterState, TableFilterAction } from "./models";
import useStyles from "./styles";

const dateFormat = "yyyy-MM";
const mask = "9999-99";

function MonthRangeFilter(props: { filter: DateFilterState; dispatcher: (action: TableFilterAction) => void }) {
	const [firstCalendarYear, setFirstCalendar] = useState<Date>(props.filter.startValue || new Date());
	const [secondCalendarYear, setSecondCalendar] = useState<Date>(startOfYear(props.filter.endValue || new Date()));
	const [startRawValue, setStartRawValue] = useState("");
	const [endRawValue, setEndRawValue] = useState("");
	const years = [firstCalendarYear, secondCalendarYear];
	const { grayTextField } = useStyles();

	useEffect(() => {
		setStartRawValue(props.filter.startValue ? format(props.filter.startValue, dateFormat) : "");
		setEndRawValue(props.filter.endValue ? format(props.filter.endValue, dateFormat) : "");
	}, [props.filter.startValue?.getTime(), props.filter.endValue?.getTime()]);

	function dispatchChangeValue(startValue: Date | null | undefined, endValue: Date | null | undefined) {
		props.dispatcher({ type: "set-values", path: props.filter.field as string, startValue, endValue });
	}

	function handleGoToNextYear(index: number) {
		if (index === 0) {
			const newDate = addYears(firstCalendarYear, 1);
			setFirstCalendar(newDate);
			if (newDate >= secondCalendarYear) {
				setSecondCalendar(addYears(newDate, 1));
			}
			return;
		}

		setSecondCalendar(addYears(secondCalendarYear, 1));
	}

	function handleGoToPreviousYear(index: number) {
		if (index === 1) {
			const newDate = addYears(secondCalendarYear, -1);
			setSecondCalendar(newDate);
			if (newDate <= firstCalendarYear) {
				setFirstCalendar(addYears(newDate, -1));
			}
			return;
		}

		setFirstCalendar(addYears(firstCalendarYear, -1));
	}

	function handleSelectMonth(date: Date) {
		if (!!props.filter.startValue === !!props.filter.endValue || (props.filter.startValue && props.filter.startValue > date)) {
			dispatchChangeValue(date, null);
		} else {
			dispatchChangeValue(props.filter.startValue, date);
		}
	}

	function handleChangeStartDate(value: string) {
		setStartRawValue(value);
		const date = parse(value, dateFormat, new Date());
		if (isValid(date)) {
			const newEndValue = isEmptyValue(props.filter.endValue) || props.filter.endValue! < date ? null : props.filter.endValue;
			const newDate = props.filter.min && date < props.filter.min ? props.filter.min : date;
			const rawStartValue = format(newDate, dateFormat);
			rawStartValue !== value && setStartRawValue(rawStartValue);
			dispatchChangeValue(newDate, newEndValue);
			setFirstCalendar(startOfMonth(newDate));
			setSecondCalendar(
				!newEndValue || startOfMonth(newDate) === newEndValue ? startOfMonth(addYears(newDate, 1)) : startOfMonth(newEndValue)
			);
		}
	}

	function handleChangeEndDate(value: string) {
		setEndRawValue(value);
		const date = parse(value, dateFormat, new Date());
		if (isValid(date)) {
			const newStartValue = isEmptyValue(props.filter.startValue) || props.filter.startValue! > date ? null : props.filter.startValue;
			const newDate = props.filter.max && date > props.filter.max ? props.filter.max : date;
			const rawEndValue = format(newDate, dateFormat);
			rawEndValue !== value && setEndRawValue(rawEndValue);
			dispatchChangeValue(props.filter.startValue, newDate);
			setSecondCalendar(startOfMonth(newDate));
			setSecondCalendar(
				!newStartValue || startOfMonth(newDate) === newStartValue
					? startOfMonth(addYears(newDate, -1))
					: startOfMonth(newStartValue)
			);
		}
	}

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" px={4} pt={3}>
				<InputMask
					mask={mask}
					placeholder={dateFormat.toLowerCase()}
					maskPlaceholder={dateFormat.toLowerCase()}
					value={startRawValue}
					disabled={props.filter.rangeType !== "custom"}
					onChange={(e) => handleChangeStartDate(e.target.value)}
				>
					<TextField
						title="date-range-input"
						variant="outlined"
						helperText=""
						InputLabelProps={{
							shrink: true,
						}}
						classes={{ root: grayTextField }}
						error={false}
					/>
				</InputMask>
				<Box mx={2}></Box>
				<InputMask
					mask={mask}
					placeholder={dateFormat.toLowerCase()}
					maskPlaceholder={dateFormat.toLowerCase()}
					value={endRawValue}
					disabled={props.filter.rangeType !== "custom"}
					onChange={(e) => handleChangeEndDate(e.target.value)}
				>
					<TextField
						title="date-range-input"
						variant="outlined"
						classes={{ root: grayTextField }}
						helperText=""
						InputLabelProps={{
							shrink: true,
						}}
						error={false}
					/>
				</InputMask>
			</Box>
			{props.filter.rangeType === "custom" && (
				<Box display="flex" justifyContent="space-between" minWidth="520px" alignItems="flex-start" padding={"24px"}>
					{years.map((d, index) => (
						<Year
							key={`${d.getTime()}.${index}`}
							year={d.getFullYear()}
							startDate={props.filter.startValue}
							endDate={props.filter.endValue}
							goToNextYear={() => handleGoToNextYear(index)}
							goToPreviousYear={() => handleGoToPreviousYear(index)}
							handleSelectMonth={(date) => handleSelectMonth(date)}
							previousDisabled={props.filter.min ? d.getFullYear() <= props.filter.min.getFullYear() : false}
							nextDisabled={props.filter.max ? d.getFullYear() >= props.filter.max.getFullYear() : false}
							minDate={props.filter.min || undefined}
							maxDate={props.filter.max || undefined}
						/>
					))}
				</Box>
			)}
		</Box>
	);
}

export default memo(MonthRangeFilter);
